import type { AxiosInstance } from "axios";
import { CheckCircle2, ExternalLink, TriangleAlert } from "lucide-react";

import { AgencyJoinForm } from "../components/AgencyJoinForm.js";
import { PublicPage } from "../components/PublicPage.js";
import { requestJoinFromInstall } from "../lib/join-requests.js";
import { Button } from "../components/ui/button.js";

/**
 * Where Shopify's install hands the merchant back — `/install/complete?shop=…&outcome=…` and
 * `/install/already-connected?shop=…`, both redirected to by the gateway's install callback. NO
 * session is assumed: a client installing through an agency's link has no Ecomally account, and a
 * merchant from the App Store may not have one yet.
 *
 * Until 2026-10-29 neither page existed, so every install ended on the sign-in screen with nothing
 * said about whether it had worked.
 */
export type InstallOutcome =
  | "created"
  | "reactivated"
  | "already-yours"
  | "already-connected"
  | "invite-invalid"
  | "store-limit";

export function installOutcomeFromUrl(location: Location): {
  readonly outcome: InstallOutcome | null;
  readonly shop: string | null;
  /** The gateway's signed claim to a store a cold install just made — lets the merchant ask an agency. */
  readonly joinToken: string | null;
} {
  const params = new URLSearchParams(location.search);
  const shop = params.get("shop");
  const joinToken = params.get("join");
  if (location.pathname === "/install/already-connected") return { outcome: "already-connected", shop, joinToken: null };
  const raw = params.get("outcome");
  const known: readonly InstallOutcome[] = ["created", "reactivated", "already-yours", "invite-invalid", "store-limit"];
  return { outcome: known.includes(raw as InstallOutcome) ? (raw as InstallOutcome) : null, shop, joinToken };
}

/** The theme editor's App embeds panel for this shop — where tracking is switched on. */
export function embedEditorUrl(shop: string): string {
  return `https://${shop}/admin/themes/current/editor?context=apps`;
}

export function InstallResultScreen({
  client,
  outcome,
  shop,
  joinToken = null,
}: {
  readonly client: AxiosInstance;
  readonly outcome: InstallOutcome | null;
  readonly shop: string | null;
  readonly joinToken?: string | null;
}): React.JSX.Element {
  const ok = outcome === "created" || outcome === "reactivated" || outcome === "already-yours";
  const store = shop ?? "your store";

  return (
    <PublicPage wide>
      <div className="flex flex-col gap-5">
        <span
          className={
            ok
              ? "flex size-11 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300"
              : "flex size-11 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300"
          }
        >
          {ok ? <CheckCircle2 className="size-6" /> : <TriangleAlert className="size-6" />}
        </span>

        {ok ? (
          <>
            <div className="flex flex-col gap-2">
              <h1 className="text-xl font-semibold">{outcome === "already-yours" ? `${store} is connected` : `${store} is installed`}</h1>
              <p className="text-muted-foreground text-sm">
                {outcome === "reactivated"
                  ? "Welcome back — its earlier data is still there."
                  : "Ecomally now receives this store's orders."}{" "}
                One step is left before visits and attribution are recorded.
              </p>
            </div>
            <ol className="bg-muted/50 flex flex-col gap-2 rounded-lg border px-4 py-3 text-sm">
              <li>
                <span className="font-medium">Switch on the Ecomally app embed</span> in your Shopify theme:{" "}
                <span className="text-muted-foreground">Online Store → Themes → Customize → App embeds</span>.
              </li>
            </ol>
            {shop !== null && (
              <Button asChild>
                <a href={embedEditorUrl(shop)} target="_blank" rel="noreferrer">
                  Open the theme editor
                  <ExternalLink />
                </a>
              </Button>
            )}
            {/*
              FLOW 1: a store a cold App Store install just made can ask its agency to take it. Offered
              only with the gateway's signed claim — the page's query string is anyone's to write.
            */}
            {joinToken !== null && (
              <section aria-labelledby="agency-join-title" className="flex flex-col gap-2 border-t pt-4">
                <h2 id="agency-join-title" className="text-sm font-semibold">
                  Is this store managed by an agency?
                </h2>
                <p className="text-muted-foreground text-sm">
                  If your agency uses Ecomally, ask them to add this store to their account — they will see its reports and
                  you will still see yours.
                </p>
                <AgencyJoinForm idPrefix="install" onSubmit={async (email) => await requestJoinFromInstall(client, joinToken, email)} />
              </section>
            )}
            <p className="text-muted-foreground text-center text-xs">
              Already use Ecomally?{" "}
              <a href="/" className="text-foreground font-medium underline underline-offset-4">
                Go to your dashboard
              </a>
            </p>
          </>
        ) : (
          <div className="flex flex-col gap-2">
            <h1 className="text-xl font-semibold">{TITLE[outcome ?? "unknown"]}</h1>
            <p className="text-muted-foreground text-sm">{body(outcome, store)}</p>
          </div>
        )}
      </div>
    </PublicPage>
  );
}

const TITLE: Record<Exclude<InstallOutcome, "created" | "reactivated" | "already-yours"> | "unknown", string> = {
  "already-connected": "This store is already connected",
  "invite-invalid": "This invite link cannot be used",
  "store-limit": "The agency's store limit is reached",
  unknown: "The install could not be completed",
};

function body(outcome: InstallOutcome | null, store: string): string {
  switch (outcome) {
    case "already-connected":
      return `${store} is already connected to an Ecomally account. Ask that account's owner to invite you to it.`;
    case "invite-invalid":
      return "The link has expired, was already used, or was withdrawn. Ask your agency to send you a new one, then install again from it.";
    case "store-limit":
      return `${store} was not added: the agency's plan has no room for another store. Ask your agency to upgrade, then install again from their link.`;
    default:
      return "Please try installing the app again. If it keeps happening, contact Ecomally support.";
  }
}
